import { useState } from 'preact/hooks';
import { api } from '../../lib/api.js';

/**
 * Inline editor for a job's job number. Read-only display by default; an
 * admin sees a tiny pencil button that swaps in an input + save/cancel.
 * Mirrors maintenance/TicketNumberEditor.jsx's exact pattern.
 */
export const JobNumberEditor = ({ job, isAdmin, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(job.jobNumber ?? '');
  const [busy, setBusy] = useState(false);

  const start = () => {
    setValue(job.jobNumber ?? '');
    setEditing(true);
  };

  const cancel = () => {
    setValue(job.jobNumber ?? '');
    setEditing(false);
  };

  const save = async () => {
    const next = value.trim();
    if (!next || next === job.jobNumber) { setEditing(false); return; }
    setBusy(true);
    try {
      await api.patch(`/jobs/${job.id}/job-number`, { jobNumber: next });
      setEditing(false);
      onSaved?.();
    } catch (err) {
      alert(err.message ?? 'Failed to save job number');
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <span class="inline-flex items-center gap-1.5">
        <span>{job.jobNumber}</span>
        {isAdmin && (
          <button
            type="button"
            onClick={start}
            title="Edit job number"
            class="rounded border border-transparent px-1 text-text-muted hover:border-border-strong hover:text-text-body focus:outline-none focus-visible:ring-1 focus-visible:ring-brand"
          >
            ✎
          </button>
        )}
      </span>
    );
  }

  return (
    <span class="inline-flex items-center gap-1.5">
      <input
        type="text"
        value={value}
        onInput={(e) => setValue(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') cancel();
        }}
        disabled={busy}
        autoFocus
        maxLength={32}
        class="w-24 rounded border border-border-strong bg-bg-base px-1.5 py-0.5 font-mono text-[11px] font-semibold text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
      />
      <button type="button" onClick={save} disabled={busy} title="Save" class="rounded border border-border-strong px-1 text-status-ok hover:bg-bg-base disabled:opacity-40">✓</button>
      <button type="button" onClick={cancel} disabled={busy} title="Cancel" class="rounded border border-border-strong px-1 text-text-muted hover:bg-bg-base disabled:opacity-40">✕</button>
    </span>
  );
};
